//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  FulfillmentItem,
  GetCampaignFulfillmentResponse,
  SamplingCampaign,
  SamplingCampaignProduct,
  SamplingCampaignReview,
} from '@apis/campaign-requests/sampling/campaigns/samplingCampaignsApiModel';
import { ClientCampaignsAggregateStatsByCampaign } from '@apis/campaign-requests/sampling/campaigns/samplingCampaignsSocialReportingModel';
import { Serialized } from '@utils/types/Serialized';

export const deserializeCampaign = (
  campaign: Serialized<SamplingCampaign>,
): SamplingCampaign => ({
  ...campaign,
  startedAt: campaign.startedAt ? new Date(campaign.startedAt) : undefined,
  expiredAt: campaign.expiredAt ? new Date(campaign.expiredAt) : undefined,
  campaignTasks: campaign.campaignTasks?.map((task) => ({
    ...task,
    embargoDate: task.embargoDate ? new Date(task.embargoDate) : undefined,
  })),
});

export const deserializeCampaignProduct = (
  product: Serialized<SamplingCampaignProduct>,
): SamplingCampaignProduct => {
  // Deserializing the insights object if it exists
  const deserializedInsights = product.insights
    ? {
        ...product.insights,
        updatedAt: product.insights.updatedAt
          ? new Date(product.insights.updatedAt)
          : undefined,
      }
    : undefined;

  return {
    ...product,
    firstReviewReceivedAt: product.firstReviewReceivedAt
      ? new Date(product.firstReviewReceivedAt)
      : undefined,
    insights: deserializedInsights,
  };
};

export const deserializeCampaignReview = (
  review: Serialized<SamplingCampaignReview>,
): SamplingCampaignReview => {
  return {
    ...review,
    submissionTime: review.submissionTime
      ? new Date(review.submissionTime)
      : undefined,
  };
};

export const deserializeFulfillmentItem = (
  item: Serialized<FulfillmentItem>,
): FulfillmentItem => {
  return {
    ...item,
    shipment: item.shipment
      ? {
          ...item.shipment,
          shipDate: item.shipment.shipDate
            ? new Date(item.shipment.shipDate)
            : undefined,
        }
      : undefined,
  };
};

export const deserializeCampaignFulfillment = (
  response: Serialized<GetCampaignFulfillmentResponse>,
): GetCampaignFulfillmentResponse => {
  return {
    ...response,
    items: response.items.map(deserializeFulfillmentItem),
  };
};

export const deserializeClientCampaignsAggregateStatsEntry = (
  campaign: Serialized<ClientCampaignsAggregateStatsByCampaign>,
): ClientCampaignsAggregateStatsByCampaign => ({
  ...campaign,
  campaignStartDate: campaign.campaignStartDate
    ? new Date(campaign.campaignStartDate)
    : undefined,
  campaignEndDate: campaign.campaignEndDate
    ? new Date(campaign.campaignEndDate)
    : undefined,
  certiniaTasks: campaign.certiniaTasks.map((task) => ({
    ...task,
    actualStartDate: task.actualStartDate
      ? new Date(task.actualStartDate)
      : undefined,
    actualEndDate: task.actualEndDate
      ? new Date(task.actualEndDate)
      : undefined,
    startDate: task.startDate ? new Date(task.startDate) : undefined,
    endDate: task.endDate ? new Date(task.endDate) : undefined,
  })),
});
